// runtime.ts — Runtime engine referential bundle, bound from relational Supabase tables (or seed JSON for scripts).
//
// The tables below are live ESM bindings: `bindEngineData()` fills them all, `patch*()` refreshes one
// referential after an admin edit, and `assertEngineDataBound()` guards the computation entry points.

import type {
  Material,
  Combination,
  Print,
  Cut,
  Lamination,
  Finishing,
  FinishingMl,
  Accessory,
  Service,
  Carpet,
  Stand,
  StandComp,
  StandMap,
  StandSubproducts,
  PaSupplier,
  WorkshopMinimum,
  MaterialRef,
  RefFourn,
  LogisticsOpt,
  InstallOpt,
  LocationOpt,
  RushOpt,
  Client,
  Compat,
  CustomSizeRate,
} from '../models/data.model';
import type { MaterialPaSource } from '../lib/pricing/pa-material';
import type { TechnicalMargins } from '../lib/ops/material-format';
import { engineSettingsFromKv, type EngineSettings } from './engine-settings';

/** Workshop matrix / mapping payload (formerly the workshops JSON file). */
export interface WorkshopsReferential {
  WORKSHOP_MAPPING: Record<string, string | null>;
  WORKSHOP_MATRIX: Record<string, { label: string; rank: number }[]>;
  MAT_CATEGORY: Record<string, string>;
  ALL_WORKSHOPS: string[];
  UNFILTERED_FAMILIES?: string[];
  /** Production lead time in working days, by workshop label (from `quote_workshops.lead_time_business_days`). */
  WORKSHOP_LEAD_TIMES?: Record<string, number>;
}

/** Full engine data snapshot after Supabase load. */
export interface EngineDataBundle {
  catalogue: readonly Material[];
  combinations: readonly Combination[];
  prints: readonly Print[];
  cuts: readonly Cut[];
  laminations: readonly Lamination[];
  finishings: readonly Finishing[];
  finishingsMl: readonly FinishingMl[];
  accessories: readonly Accessory[];
  servicesCatalogue: readonly Service[];
  carpets: readonly Carpet[];
  moqTiers: readonly number[];
  stands: readonly Stand[];
  finishedProducts: readonly Stand[];
  customSizeRates: readonly CustomSizeRate[];
  transfers: readonly Stand[];
  historicalTwins: Readonly<Record<string, string>>;
  standComponents: Readonly<Record<string, StandComp>>;
  standMap: Readonly<Record<string, StandMap>>;
  standSubproducts: Readonly<Record<string, StandSubproducts>>;
  paSuppliers: readonly PaSupplier[];
  workshopMinimums: Readonly<Record<string, WorkshopMinimum>>;
  paNotes: Readonly<Record<string, string>>;
  serviceKey: Readonly<Record<string, string>>;
  cutClassPa: readonly string[];
  materialRefs: Readonly<Record<string, MaterialRef>>;
  formatCatalog: readonly string[];
  matFormats: Readonly<Record<string, readonly string[]>>;
  defaultMatFormat: Readonly<Record<string, string>>;
  supplierRefs: Readonly<Record<string, RefFourn>>;
  materialSuppliersList: readonly string[];
  compat: Compat;
  materialFamily: Readonly<Record<string, string>>;
  stdPrintMap: Readonly<Record<string, string>>;
  familyLabel: Readonly<Record<string, string>>;
  logisticsOpts: readonly LogisticsOpt[];
  installOpts: readonly InstallOpt[];
  rentalOpts: readonly LocationOpt[];
  rushOpts: readonly RushOpt[];
  pickupLocations: readonly string[];
  autodim: Readonly<Record<string, string>>;
  clients: readonly Client[];
  stockUpz: readonly string[];
  workshops: WorkshopsReferential;
  /** Business parameters read from the settings tables (rate card, rules, thresholds...). Required, no default. */
  settings: EngineSettings;
  /**
   * Material purchase-price source read from `material_suppliers` (gross PA, material-specific waste coeff,
   * update date) and `waste_coeff_by_family`. Absent = embedded reference data + per-nature defaults.
   */
  materialPaSource?: MaterialPaSource;
}

let bound = false;

export let CATALOGUE: readonly Material[] = [];
export let COMBINATIONS: readonly Combination[] = [];
export let PRINTS: readonly Print[] = [];
export let CUTS: readonly Cut[] = [];
export let LAMINATIONS: readonly Lamination[] = [];
export let FINISHINGS: readonly Finishing[] = [];
export let FINISHINGS_ML: readonly FinishingMl[] = [];
export let ACCESSORIES: readonly Accessory[] = [];
export let SERVICES_CAT: readonly Service[] = [];
export let CARPETS: readonly Carpet[] = [];
export let MOQ_TIERS: readonly number[] = [];
export let STANDS: readonly Stand[] = [];
export let FINISHED_PRODUCTS: readonly Stand[] = [];
export let CUSTOM_SIZE_RATES: readonly CustomSizeRate[] = [];
export let TRANSFERS: readonly Stand[] = [];
export let HISTORICAL_TWINS: Readonly<Record<string, string>> = {};
export let STAND_COMPONENTS: Readonly<Record<string, StandComp>> = {};
export let STAND_MAP: Readonly<Record<string, StandMap>> = {};
export let STAND_SUBPRODUCTS: Readonly<Record<string, StandSubproducts>> = {};
export let PA_SUPPLIERS: readonly PaSupplier[] = [];
export let WORKSHOP_MINIMUMS: Readonly<Record<string, WorkshopMinimum>> = {};
export let PA_NOTES: Readonly<Record<string, string>> = {};
export let SERVICE_KEY: Readonly<Record<string, string>> = {};
export let CUT_CLASS_PA: readonly string[] = [];
export let MATERIAL_REFS: Readonly<Record<string, MaterialRef>> = {};
export let FORMAT_CATALOG: readonly string[] = [];
export let MAT_FORMATS: Readonly<Record<string, readonly string[]>> = {};
export let DEFAULT_MAT_FORMAT: Readonly<Record<string, string>> = {};
export let SUPPLIER_REFS: Readonly<Record<string, RefFourn>> = {};
export let MATERIAL_SUPPLIERS_LIST: readonly string[] = [];
export let COMPAT: Compat = { print: {}, cut: {}, finishing: {}, lamination: {} } as Compat;
export let MATERIAL_FAMILY: Readonly<Record<string, string>> = {};
export let STD_PRINT_MAP: Readonly<Record<string, string>> = {};
export let FAMILY_LABEL: Readonly<Record<string, string>> = {};
export let LOGISTICS_OPTS: readonly LogisticsOpt[] = [];
export let INSTALL_OPTS: readonly InstallOpt[] = [];
export let RENTAL_OPTS: readonly LocationOpt[] = [];
export let RUSH_OPTS: readonly RushOpt[] = [];
export let PICKUP_LOCATIONS: readonly string[] = [];
export let AUTODIM: Readonly<Record<string, string>> = {};
export let CLIENTS: readonly Client[] = [];
export let STOCK_UPZ: readonly string[] = [];

export let WORKSHOP_MATRIX: Readonly<
  Record<string, readonly { label: string; rank: number }[]>
> = {};
export let WORKSHOP_MAPPING: Readonly<Record<string, string | null>> = {};
export let MATERIAL_CATEGORY_MAP: Readonly<Record<string, string>> = {};
export let ALL_WORKSHOPS: readonly string[] = [];
export let UNFILTERED_FAMILIES: ReadonlySet<string> = new Set();
/** Production lead time (working days) of the workshops that have one, by workshop label. */
export let WORKSHOP_LEAD_TIMES: Readonly<Record<string, number>> = {};
let engineSettingsBound: EngineSettings | undefined = undefined;

/**
 * The business parameters bound from the settings tables.
 * @returns The bound settings.
 * @throws Error when the engine data (hence the settings) is not bound: there is no default value.
 */
export function engineSettings(): EngineSettings {
  if (!engineSettingsBound) {
    throw new Error('Quote engine settings are not bound: load the settings tables and call bindEngineData() first.');
  }
  return engineSettingsBound;
}
/** Material purchase-price source injected into compute() by default (see ComputeOptions.sourcePaMaterial). */
export let MATERIAL_PA_SOURCE: MaterialPaSource | undefined = undefined;

/**
 * True when {@link bindEngineData} has been called successfully.
 * @returns Whether the engine referentials are bound
 */
export function isEngineDataBound(): boolean {
  return bound;
}

/**
 * Guard for the computation entry points: pricing against unbound (empty) referentials would silently
 * produce wrong prices, so it must fail loudly instead.
 * @throws Error when {@link bindEngineData} has not been called yet
 */
export function assertEngineDataBound(): void {
  if (!bound) {
    throw new Error(
      'Quote engine data is not bound: load the referentials and call bindEngineData() before computing.',
    );
  }
}

/** Price-bearing referentials that an admin tariff edit can refresh without a full rebind. */
export interface CataloguePatch {
  materials: readonly Material[];
  prints: readonly Print[];
  cuts: readonly Cut[];
  laminations: readonly Lamination[];
  finishings: readonly Finishing[];
  finishingsMl: readonly FinishingMl[];
  /** Accessories read from the same tables; kept as bound when omitted. */
  accessories?: readonly Accessory[];
  /** Service catalogue items; kept as bound when omitted. */
  servicesCatalogue?: readonly Service[];
}

/**
 * Overwrites the runtime price-bearing tables (materials, prints, cuts, laminations, finishings, and the
 * accessories / services when given) from freshly loaded `catalog_items` / `quote_components` rows.
 * `ACTIVE_CATALOGUE` reads these bindings through getters, so every function defaulting to it sees the new
 * prices at once. Call after an admin tariff edit (the full {@link bindEngineData} stays for structural edits).
 * @param patch Tables rebuilt from the database, with the edited prices
 */
export function patchCatalogue(patch: CataloguePatch): void {
  CATALOGUE = patch.materials;
  PRINTS = patch.prints;
  CUTS = patch.cuts;
  LAMINATIONS = patch.laminations;
  FINISHINGS = patch.finishings;
  FINISHINGS_ML = patch.finishingsMl;
  if (patch.accessories) ACCESSORIES = patch.accessories;
  if (patch.servicesCatalogue) SERVICES_CAT = patch.servicesCatalogue;
}

/**
 * Overwrites the runtime families×workshops matrix from live `workshop_matrix` rows.
 * Call after admin save so menus / validation match the DB.
 * @param lines Rows of `workshop_matrix` (family, workshop, rank)
 */
export function patchWorkshopMatrix(
  lines: readonly { family: string; workshop: string; rank: number }[],
): void {
  const m: Record<string, { label: string; rank: number }[]> = {};
  for (const l of lines) {
    (m[l.family] ??= []).push({ label: l.workshop, rank: l.rank });
  }
  for (const fam of Object.keys(m)) {
    m[fam]!.sort((a, b) => a.rank - b.rank || a.label.localeCompare(b.label));
  }
  WORKSHOP_MATRIX = m;
}

/**
 * Overwrites runtime PA_SUPPLIERS from live `pa_grids` rows (no full rebind).
 * Call after admin PA edits so §23 menus / lookups match the DB.
 * @param grids Supplier purchase-price grids read from `pa_grids`
 */
export function patchPaSuppliers(grids: readonly PaSupplier[]): void {
  PA_SUPPLIERS = grids;
}

/**
 * Overwrites the runtime material purchase-price source (gross PA, waste coefficients) after an admin edit.
 * @param source Source rebuilt from `material_suppliers` / `waste_coeff_by_family`, or undefined to fall back
 *   on the embedded reference data
 */
export function patchMaterialPaSource(source: MaterialPaSource | undefined): void {
  MATERIAL_PA_SOURCE = source;
}

/**
 * Replaces the bound business settings after an admin edit of the settings tables (no full rebind).
 * @param settings Settings rebuilt from the tables
 * @throws Error when the engine data is not bound yet
 */
export function patchEngineSettings(settings: EngineSettings): void {
  engineSettings();
  engineSettingsBound = settings;
}

/**
 * Binds the business settings alone, without any referential: lets a unit test price a part of the engine that reads
 * the settings only. The application never calls it (it binds everything with {@link bindEngineData}).
 * @param settings Settings of the test
 */
export function bindEngineSettingsForTest(settings: EngineSettings): void {
  engineSettingsBound = settings;
}

/** Undoes {@link bindEngineSettingsForTest}, so that the specs that follow find the engine as they expect it. */
export function unbindEngineSettingsForTest(): void {
  engineSettingsBound = undefined;
}

/**
 * Binds Supabase-loaded referentials into module exports (live ESM bindings).
 * Must be called once before any pricing / menu / catalogue computation.
 * @param bundle Snapshot built from relational tables (or seed scripts)
 */
export function bindEngineData(bundle: EngineDataBundle): void {
  CATALOGUE = bundle.catalogue;
  COMBINATIONS = bundle.combinations;
  PRINTS = bundle.prints;
  CUTS = bundle.cuts;
  LAMINATIONS = bundle.laminations;
  FINISHINGS = bundle.finishings;
  FINISHINGS_ML = bundle.finishingsMl;
  ACCESSORIES = bundle.accessories;
  SERVICES_CAT = bundle.servicesCatalogue;
  CARPETS = bundle.carpets;
  MOQ_TIERS = bundle.moqTiers;
  STANDS = bundle.stands;
  FINISHED_PRODUCTS = bundle.finishedProducts;
  CUSTOM_SIZE_RATES = bundle.customSizeRates;
  TRANSFERS = bundle.transfers;
  HISTORICAL_TWINS = bundle.historicalTwins;
  STAND_COMPONENTS = bundle.standComponents;
  STAND_MAP = bundle.standMap;
  STAND_SUBPRODUCTS = bundle.standSubproducts;
  PA_SUPPLIERS = bundle.paSuppliers;
  WORKSHOP_MINIMUMS = bundle.workshopMinimums;
  PA_NOTES = bundle.paNotes;
  SERVICE_KEY = bundle.serviceKey;
  CUT_CLASS_PA = bundle.cutClassPa;
  MATERIAL_REFS = bundle.materialRefs;
  FORMAT_CATALOG = bundle.formatCatalog;
  MAT_FORMATS = bundle.matFormats;
  DEFAULT_MAT_FORMAT = bundle.defaultMatFormat;
  SUPPLIER_REFS = bundle.supplierRefs;
  MATERIAL_SUPPLIERS_LIST = bundle.materialSuppliersList;
  COMPAT = bundle.compat;
  MATERIAL_FAMILY = bundle.materialFamily;
  STD_PRINT_MAP = bundle.stdPrintMap;
  FAMILY_LABEL = bundle.familyLabel;
  LOGISTICS_OPTS = bundle.logisticsOpts;
  INSTALL_OPTS = bundle.installOpts;
  RENTAL_OPTS = bundle.rentalOpts;
  RUSH_OPTS = bundle.rushOpts;
  PICKUP_LOCATIONS = bundle.pickupLocations;
  AUTODIM = bundle.autodim;
  CLIENTS = bundle.clients;
  STOCK_UPZ = bundle.stockUpz;
  WORKSHOP_MATRIX = bundle.workshops.WORKSHOP_MATRIX;
  WORKSHOP_MAPPING = bundle.workshops.WORKSHOP_MAPPING;
  MATERIAL_CATEGORY_MAP = bundle.workshops.MAT_CATEGORY;
  ALL_WORKSHOPS = bundle.workshops.ALL_WORKSHOPS;
  UNFILTERED_FAMILIES = new Set(bundle.workshops.UNFILTERED_FAMILIES ?? []);
  WORKSHOP_LEAD_TIMES = bundle.workshops.WORKSHOP_LEAD_TIMES ?? {};
  engineSettingsBound = bundle.settings;
  MATERIAL_PA_SOURCE = bundle.materialPaSource;
  bound = true;
}

/**
 * Restores the pristine, unbound state of the runtime referentials: every table empty and
 * {@link isEngineDataBound} false again. Intended for tests (isolating suites that bind fixtures) and for a
 * clean teardown on sign-out.
 */
export function resetEngineData(): void {
  CATALOGUE = [];
  COMBINATIONS = [];
  PRINTS = [];
  CUTS = [];
  LAMINATIONS = [];
  FINISHINGS = [];
  FINISHINGS_ML = [];
  ACCESSORIES = [];
  SERVICES_CAT = [];
  CARPETS = [];
  MOQ_TIERS = [];
  STANDS = [];
  FINISHED_PRODUCTS = [];
  CUSTOM_SIZE_RATES = [];
  TRANSFERS = [];
  HISTORICAL_TWINS = {};
  STAND_COMPONENTS = {};
  STAND_MAP = {};
  STAND_SUBPRODUCTS = {};
  PA_SUPPLIERS = [];
  WORKSHOP_MINIMUMS = {};
  PA_NOTES = {};
  SERVICE_KEY = {};
  CUT_CLASS_PA = [];
  MATERIAL_REFS = {};
  FORMAT_CATALOG = [];
  MAT_FORMATS = {};
  DEFAULT_MAT_FORMAT = {};
  SUPPLIER_REFS = {};
  MATERIAL_SUPPLIERS_LIST = [];
  COMPAT = { print: {}, cut: {}, finishing: {}, lamination: {} } as Compat;
  MATERIAL_FAMILY = {};
  STD_PRINT_MAP = {};
  FAMILY_LABEL = {};
  LOGISTICS_OPTS = [];
  INSTALL_OPTS = [];
  RENTAL_OPTS = [];
  RUSH_OPTS = [];
  PICKUP_LOCATIONS = [];
  AUTODIM = {};
  CLIENTS = [];
  STOCK_UPZ = [];
  WORKSHOP_MATRIX = {};
  WORKSHOP_MAPPING = {};
  MATERIAL_CATEGORY_MAP = {};
  ALL_WORKSHOPS = [];
  UNFILTERED_FAMILIES = new Set();
  WORKSHOP_LEAD_TIMES = {};
  engineSettingsBound = undefined;
  MATERIAL_PA_SOURCE = undefined;
  bound = false;
}

/** Empty compatibility matrix (every axis empty) — the safe default of an absent `compat` payload. */
const EMPTY_COMPAT: Compat = { print: {}, cut: {}, finishing: {}, lamination: {} } as Compat;

/** Empty workshops referential — the safe default of an absent `workshops` payload. */
const EMPTY_WORKSHOPS: WorkshopsReferential = {
  WORKSHOP_MAPPING: {},
  WORKSHOP_MATRIX: {},
  MAT_CATEGORY: {},
  ALL_WORKSHOPS: [],
};

/**
 * Builds an {@link EngineDataBundle} from keyed seed payloads (local JSON files).
 * Used by supabase seed/import scripts — not by the admin runtime (tables).
 * The core payloads (`catalogue`, `combinations`, `components`) and the business settings (`engine_settings`:
 * `{ kv, technicalMargins }`, no default) are required; every other payload
 * defaults to an empty referential, so a partial seed yields a safe (empty) table rather than `undefined`.
 * @param byKey Map key → payload object
 * @returns The engine bundle, ready for {@link bindEngineData}
 * @throws Error when a required core payload is absent
 */
export function bundleFromReferentialPayloads(
  byKey: ReadonlyMap<string, Record<string, unknown>>,
): EngineDataBundle {
  /**
   * Reads a core payload that must be present.
   * @param key Payload key
   * @returns The payload
   * @throws Error when the payload is absent
   */
  const req = (key: string): Record<string, unknown> => {
    const p = byKey.get(key);
    if (!p) throw new Error(`Missing seed referential: "${key}"`);
    return p;
  };
  /**
   * Reads an optional payload.
   * @param key Payload key
   * @returns The payload, or an empty object when absent
   */
  const opt = (key: string): Record<string, unknown> => byKey.get(key) ?? {};

  const catalogue = req('catalogue');
  const combinations = req('combinations');
  const components = req('components');
  const carpets = opt('carpets');
  const stands = opt('stands');
  const transfers = opt('transfers');
  const twins = opt('twins');
  const pa = opt('pa_suppliers');
  const formats = opt('formats');
  const compat = opt('compat');
  const services = opt('services');
  const clients = opt('clients');
  const settingsPayload = req('engine_settings');
  const workshops = (byKey.get('workshops') as unknown as WorkshopsReferential | undefined) ?? EMPTY_WORKSHOPS;

  return {
    catalogue: (catalogue['CATALOGUE'] as Material[]) ?? [],
    combinations: (combinations['COMBINATIONS'] as Combination[]) ?? [],
    prints: (components['PRINTS'] as Print[]) ?? [],
    cuts: (components['CUTS'] as Cut[]) ?? [],
    laminations: (components['LAMINATIONS'] as Lamination[]) ?? [],
    finishings: (components['FINISHINGS'] as Finishing[]) ?? [],
    finishingsMl: (components['FINISHINGS_ML'] as FinishingMl[]) ?? [],
    accessories: (components['ACCESSORIES'] as Accessory[]) ?? [],
    servicesCatalogue: (components['SERVICES_CAT'] as Service[]) ?? [],
    carpets: (carpets['CARPETS'] as Carpet[]) ?? [],
    moqTiers: (carpets['MOQ_TIERS'] as number[]) ?? [],
    stands: (stands['STANDS'] as Stand[]) ?? [],
    finishedProducts: (stands['FINISHED_PRODUCTS'] as Stand[]) ?? [],
    customSizeRates: (stands['CUSTOM_SIZE_RATES'] as CustomSizeRate[]) ?? [],
    transfers: (transfers['TRANSFERS'] as Stand[]) ?? [],
    historicalTwins: (twins['HISTORICAL_TWINS'] as Record<string, string>) ?? {},
    standComponents: (stands['STAND_COMPONENTS'] as Record<string, StandComp>) ?? {},
    standMap: (stands['STAND_MAP'] as Record<string, StandMap>) ?? {},
    standSubproducts: (stands['STAND_SUBPRODUCTS'] as Record<string, StandSubproducts>) ?? {},
    paSuppliers: (pa['PA_SUPPLIERS'] as PaSupplier[]) ?? [],
    workshopMinimums: (pa['WORKSHOP_MINIMUMS'] as Record<string, WorkshopMinimum>) ?? {},
    paNotes: (pa['PA_NOTES'] as Record<string, string>) ?? {},
    serviceKey: (pa['SERVICE_KEY'] as Record<string, string>) ?? {},
    cutClassPa: (pa['CUT_CLASS_PA'] as string[]) ?? [],
    materialRefs: (pa['MATERIAL_REFS'] as Record<string, MaterialRef>) ?? {},
    formatCatalog: (formats['FORMAT_CATALOG'] as string[]) ?? [],
    matFormats: (formats['MAT_FORMATS'] as Record<string, readonly string[]>) ?? {},
    defaultMatFormat: (formats['DEFAULT_MAT_FORMAT'] as Record<string, string>) ?? {},
    supplierRefs: (formats['SUPPLIER_REFS'] as Record<string, RefFourn>) ?? {},
    materialSuppliersList: (formats['MATERIAL_SUPPLIERS_LIST'] as string[]) ?? [],
    compat: (compat['COMPAT'] as Compat | undefined) ?? EMPTY_COMPAT,
    materialFamily: (compat['MATERIAL_FAMILY'] as Record<string, string>) ?? {},
    stdPrintMap: (compat['STD_PRINT_MAP'] as Record<string, string>) ?? {},
    familyLabel: (compat['FAMILY_LABEL'] as Record<string, string>) ?? {},
    logisticsOpts: (services['LOGISTICS_OPTS'] as LogisticsOpt[]) ?? [],
    installOpts: (services['INSTALL_OPTS'] as InstallOpt[]) ?? [],
    rentalOpts: (services['RENTAL_OPTS'] as LocationOpt[]) ?? [],
    rushOpts: (services['RUSH_OPTS'] as RushOpt[]) ?? [],
    pickupLocations: (services['PICKUP_LOCATIONS'] as string[]) ?? [],
    autodim: (services['AUTODIM'] as Record<string, string>) ?? {},
    clients: (clients['CLIENTS'] as Client[]) ?? [],
    stockUpz: (clients['STOCK_UPZ'] as string[]) ?? [],
    workshops,
    settings: engineSettingsFromKv(
      (settingsPayload['kv'] as Record<string, unknown>) ?? {},
      settingsPayload['technicalMargins'] as TechnicalMargins,
    ),
  };
}
